// Specific Bullhorn API entities
// https://bullhorn.github.io/rest-api-docs/entityref.html

import { z } from 'zod';
import * as R from 'remeda';
import { DayOfWeek } from '../../dates.js';
export const BHDate = z.number().int().describe('Unix timestamp (milliseconds)');

// Base shape for most entities
const BaseEntity = z.looseObject({
  id: z.number().int(),
  name: z.string().optional(),
  dateAdded: BHDate.optional(),
  dateLastModified: BHDate.optional(),
});

const BaseEntityWithName = BaseEntity.required({ name: true });

export const UnknownEntity = BaseEntity;
export type UnknownEntity = z.infer<typeof UnknownEntity>;

const toMany = <Z extends z.ZodType>(entity: Z) =>
  z.object({
    total: z.number().int(),
    data: z.array(entity),
  });

export const ToManyValue = toMany(UnknownEntity);
export type ToManyValue<T extends UnknownEntity = UnknownEntity> = {
  total: number;
  data: T[];
};

const flatToMany = <Z extends typeof UnknownEntity>(entity: Z) => z.array(entity);
export const FlatToManyValue = flatToMany(UnknownEntity);

export const Address = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  countryID: z.unknown().nullish(),
});
export type Address = z.infer<typeof Address>;

// The address sub-fields we write; excludes Bullhorn-derived countryCode/countryName/timezone.
export const WritableAddress = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  // Resolved from the detected country name at write time (see resolveCountry).
  countryID: z.number().nullish(),
});
export type WritableAddress = z.infer<typeof WritableAddress>;

// Human-meaningful address sub-fields (excludes countryID); the single source of truth for sub-field lists.
export const ADDRESS_VALUE_SUBFIELDS = ['address1', 'address2', 'city', 'state', 'zip'] as const;
export type AddressValueSubfield = (typeof ADDRESS_VALUE_SUBFIELDS)[number];

export interface AddressSearchSubfield {
  key: string; // combined as `${composite}.${key}`, e.g. address.city
  label: string;
  queryType: 'string' | 'number';
  optionsType?: string; // present => filtered via a reference picker on this Bullhorn options list
}

const ADDRESS_SUBFIELD_LABELS: Record<AddressValueSubfield, string> = {
  address1: 'Address',
  address2: 'Address 2',
  city: 'City',
  state: 'State',
  zip: 'Zip',
};

// Searchable address sub-fields: the writable string sub-fields plus country (filtered by countryID via a picker).
export const ADDRESS_SEARCH_SUBFIELDS: readonly AddressSearchSubfield[] = [
  ...ADDRESS_VALUE_SUBFIELDS.map(
    (key): AddressSearchSubfield => ({
      key,
      label: ADDRESS_SUBFIELD_LABELS[key],
      queryType: 'string',
    })
  ),
  { key: 'countryID', label: 'Country', queryType: 'number', optionsType: 'Country' },
];
// Compile-time guard: every entry must be a writable address key other than countryID.
const _addressSubfieldKeyCheck: ReadonlyArray<Exclude<keyof WritableAddress, 'countryID'>> =
  ADDRESS_VALUE_SUBFIELDS;
void _addressSubfieldKeyCheck;

export const ClientCorporation = BaseEntity.safeExtend({
  /** Name of the company. */
  name: z.string(),

  /** Status of the business relationship with this company; for example, Prospect, Active, and so forth. */
  status: z.string().nullable(),
  address: Address,
  /** Owners of the ClientContacts for this ClientCorporation. */
  owners: ToManyValue,
  /** ClientCorporation that is a parent of this one. */
  parentClientCorporation: UnknownEntity.nullable(),
  /** ClientCorporations that are children of this one. */
  childClientCorporations: ToManyValue.nullable(),

  /** Timestamp Date on which this record was created in the Bullhorn system. */
  dateAdded: BHDate,
  /** Timestamp Date on which the ClientCorporation was last modified. */
  dateLastModified: BHDate,

  /** ClientContacts who work at this ClientCorporation. */
  clientContacts: ToManyValue.nullable(),
  /**
   *  Department of the Staffing Firm that has primary responsibility for this client (for example, a regional office).
   *  Default value is primary department of user who adds the ClientCorporation.
   *  */
  department: UnknownEntity.nullable(),
  /** Status of the ClientCorporation’s current ownership (for example, Public, Private). */
  ownerShip: z.string().nullable(),
  /** ClientCorporation’s annual revenue in millions of U.S. dollars. */
  annualRevenue: z.number().nullable(),
  /** Address that contains the address to which bills should be sent for this ClientCorporation. */
  billingAddress: Address.nullable(),
  /** Name of the person to whom bills should be sent. */
  billingContact: z.string().nullable(),
  /** Frequency with which bills should be sent to the ClientCorporation: for example, Weekly, Bi-Weekly, Semi-Monthly, Monthly. */
  billingFrequency: z.string().nullable(),
  /** Phone number of the billing contact person. */
  billingPhone: z.string().nullable(),
  /** Comma-separated list of BusinessSectors in which the ClientCorporation operates. See field map data for a list of possible values. */
  businessSectorList: z.string().or(z.string().array()).nullable(),
  /** Text description of the company. */
  companyDescription: z.string().nullable(),
  /** Company’s website URL. May use http:// but not required. */
  companyURL: z.string().nullable(),
  /** Company’s major competitors. */
  competitors: z.string().nullable(),
  /** Text description of the corporate culture. */
  culture: z.string().nullable(),
  /** Timestamp Date on which the ClientCorporation was founded. */
  dateFounded: BHDate.nullable(),
  /** External identifier for the record, used for migrations and back-office Integration. */
  externalID: z.string().nullable(),
  /** Tax exemption status for the ClientCorporation. Options are: 1=Exempt, 2=Non-Exempt, 3=Self-Pay */
  exemptionStatus: z.string().or(z.number()).nullable(),
  /** Fee, expressed as a percentage, that this ClientCorporation will pay for each Placement. */
  feeArrangement: z.number().nullable(),
  /** Current funding status of the ClientCorporation. */
  funding: z.string().nullable(),
  /** Comma-separated list of industries in which the company operates. See field map data for the list used by a particular company. */
  industryList: z.string().or(z.string().array()).nullable(),
  /** Configurable field to be passed to a back-office system to indicate how to group invoices for this company (Per Placement, Per Billing Contact, and so forth.) */
  invoiceFormat: z.string().nullable(),
  /** Leads associated with this ClientCorporation. */
  leads: ToManyValue.nullable(),
  /** Free text field for entering any comments or notes about the company. */
  notes: z.string().nullable(),
  /** Total number of people employed by the company. */
  numEmployees: z.number().int().nullable(),
  /** Total number of offices for the ClientCorporation. */
  numOffices: z.number().int().nullable(),
  /** Main phone number for the ClientCorporation. */
  phone: z.string().nullable(),
  /** Estimated annual revenue in millions for the company. */
  revenue: z.string().nullable(),
  /** Certifications that Candidates should have. */
  requirements: ToManyValue.nullable(),
  /** Tax rate for the company expressed in percentage: for example, 28%. Passed to a back-office system if applicable. */
  taxRate: z.number().nullable(),
  /** Stock market ticker symbol for the company. */
  tickerSymbol: z.string().nullable(),
  /** Day of the week on which the work week starts for this company. 1=Sunday, 2=Monday, and so forth. */
  workWeekStart: z.number().int().nullable(),

  /** ClientCorporationCertifications associated with this corporation. */
  clientCorporationCertifications: ToManyValue.nullable(),
  /** Invoice groups associated with this ClientCorporation. */
  invoiceGroups: ToManyValue.nullable(),
  /** Invoices associated with this corporation. */
  invoices: ToManyValue.nullable(),
  /** Rate cards associated with this ClientCorporation. */
  rateCards: ToManyValue.nullable(),
})
  .partial()
  .required({ id: true });
export type ClientCorporation = z.infer<typeof ClientCorporation>;

export const PersonSubType = z.enum(['CorporateUser', 'Candidate', 'ClientContact']);
export type PersonSubType = z.infer<typeof PersonSubType>;

export const Person = BaseEntity.safeExtend({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  _subtype: PersonSubType.nullish(),
});
export type Person = z.infer<typeof Person>;

export const Candidate = Person.safeExtend({
  _subtype: z.literal('Candidate').nullish(),
  description: z.string().nullish().describe("Usually the candidate's resume"),
  category: UnknownEntity.nullish(),
});
export type Candidate = z.infer<typeof Candidate>;

export const ClientContact = Person.safeExtend({
  _subtype: z.literal('ClientContact').nullish(),
  clientCorporation: ClientCorporation.nullish(),
});
export type ClientContact = z.infer<typeof ClientContact>;

export const CorporateUser = Person.safeExtend({
  _subtype: z.literal('CorporateUser').nullish(),
  username: z.string().nullish(),
  branch: UnknownEntity.nullish(),
  branches: ToManyValue.nullish(),
  companyName: z.string().nullish(),
  departments: ToManyValue.nullish(),
  departmentIdList: z.string().or(z.string().array()).nullish(),
  primaryDepartment: UnknownEntity.nullish(),
  emailSignature: z.string().nullish(),
  enabled: z.boolean().nullish(),
  externalEmail: z.string().nullish(),
  isLockedOut: z.boolean().nullish(),
  occupation: z.string().nullish(),
  userType: UnknownEntity.nullish(),
  privateLabel: UnknownEntity.nullish(),
  userDateAdded: BHDate.nullish(),
});
export type CorporateUser = z.infer<typeof CorporateUser>;

/**
 * Bullhorn AtsEmail entity
 */
export const UserMessage = BaseEntity.safeExtend({
  subject: z.string().nullish(),
  comments: z.string().nullish(),
  externalFrom: z.string().nullish(),
  externalTo: z.string().nullish(),
  externalCC: z.string().nullish(),
  externalBCC: z.string().nullish(),
  emailRecipients: z.string().nullish(),

  sender: Person.nullish(),
  ccRecipients: toMany(Person).nullish(),
  toRecipients: toMany(Person).nullish(),
  personRecipients: toMany(Person).nullish(),
  distributionLists: ToManyValue.nullish(),
  messageFiles: ToManyValue.nullish(),

  isPrivate: z.boolean().nullish(),
  smtpHeaderData: z.string().nullish(),
  smtpSendDate: BHDate.nullish(),
  smtpReceiveDate: BHDate.nullish(),
  threadID: z.string().nullish(),

  // Unused fields
  invoiceStatements: ToManyValue.nullish(),
  isSenderChecked: z.boolean().nullish(),
  isSenderDeleted: z.boolean().nullish(),
  folder: z.string().nullish(),
  priority: z.number().nullish(),
  messageSize: z.number().nullish(),
  userMessageUUID: z.string().nullish(),
});
export type UserMessage = z.infer<typeof UserMessage>;

export const JobOrder = BaseEntity.safeExtend({
  title: z.string().nullish(),
  address: Address.nullish(),
  status: z.string().nullish(),
  salary: z.number().nullish(),
  // get placements() { return toMany(Placement).nullish() },
  placements: ToManyValue.nullish(),
  // get submissions() { return toMany(JobSubmission).nullish() },
  submissions: ToManyValue.nullish(),
  startDate: BHDate.nullish(),
  dateEnd: BHDate.nullish(),
  description: z.string().nullish(),

  dateLastPublished: BHDate.nullish(),
  dateClosed: BHDate.nullish(),
  educationDegree: z.string().nullish(),
  employmentType: z.string().nullish(),
  isOpen: z.boolean().nullish(),
  isPublic: z.boolean().nullish(),
  owner: CorporateUser.nullish(),
  skills: z.string().nullish(),

  clientCorporation: ClientCorporation.nullish(),
  clientContact: ClientContact.nullish(),
});
export type JobOrder = z.infer<typeof JobOrder>;

export const JobSubmission = BaseEntity.safeExtend({
  status: z.string().nullish(),
  comments: z.string().nullish(),
  candidate: Person.nullish(),
  jobOrder: JobOrder.nullish(),
  startDate: BHDate.nullish(),
  endDate: BHDate.nullish(),
  owners: toMany(Person).nullish(),
  dateWebResponse: BHDate.nullish(),
  billRate: z.number().nullish(),
  payRate: z.number().nullish(),
  salary: z.number().nullish(),
  sendingUser: Person.nullish(),
  source: z.string().nullish(),
});
export type JobSubmission = z.infer<typeof JobSubmission>;

export const Placement = BaseEntity.safeExtend({
  candidate: Person.nullish(),
  status: z.string().nullish(),
  comments: z.string().nullish(),
  dateBegin: BHDate.nullish(),
  dateEnd: BHDate.nullish(),
  durationWeeks: z.number().nullish(),
  employmentType: z.string().nullish(),
  jobOrder: JobOrder.nullish(),
  jobSubmission: JobSubmission.nullish(),
  owner: Person.nullish(),
  payRate: z.number().nullish(),
  salary: z.number().nullish(),
  salaryUnit: z.string().nullish(),
  clientBillRate: z.number().nullish(),
});
export type Placement = z.infer<typeof Placement>;

export const NoteEntity = BaseEntity.safeExtend({
  note: UnknownEntity.nullish(),
  targetEntityID: z.int().nullish(),
  targetEntityName: z.string().nullish(),
});
export type NoteEntity = z.infer<typeof NoteEntity>;

export const Note = BaseEntity.safeExtend({
  action: z.string().nullish(),
  candidates: toMany(Person).nullish(),
  clientContacts: toMany(Person).nullish(),
  commentingPerson: Person.nullish(),
  comments: z.string().nullish(),
  corporateUsers: toMany(Person).nullish(),
  entities: toMany(NoteEntity).nullish(),
  externalID: z.string().nullish(),
  isDeleted: z.boolean().nullish(),
  jobOrder: JobOrder.nullish(),
  jobOrders: toMany(JobOrder).nullish(),
  jobShifts: ToManyValue.nullish(),
  leads: ToManyValue.nullish(),
  linkedInID: z.string().nullish(),
  minutesSpent: z.number().nullish(),
  opportunities: ToManyValue.nullish(),
  people: toMany(Person).nullish(),
  personReference: Person.nullish(),
  placements: toMany(Placement).nullish(),
  primaryDepartmentName: z.string().nullish(),
});
export type Note = z.infer<typeof Note>;

export const EditHistoryFieldChange = BaseEntity.safeExtend({
  columnName: z.string().nullish().describe('Database column name for the value that was changed.'),
  display: z.string().nullish().describe('Field label, calculated at the time of EditHistory'),
  editHistory: UnknownEntity.nullish(),
  newValue: z.string().nullish(),
  oldValue: z.string().nullish(),
});
export type EditHistoryFieldChange = z.infer<typeof EditHistoryFieldChange>;

export const EditHistory = BaseEntity.safeExtend({
  auditTrail: z.string().nullish(),
  fieldChanges: toMany(EditHistoryFieldChange).nullish(),
  modifyingPerson: Person.nullish(),
  targetEntity: UnknownEntity.nullish(),
  transactionID: z.string().nullish(),
});
export type EditHistory = z.infer<typeof EditHistory>;

export type FlattenRefs<T> = {
  [K in keyof T]: T[K] extends { data: Array<infer U> } | null | undefined ?
    Array<U> | null | undefined
  : T[K];
};

/** Bullhorn returns TO_MANY associations as `{ total, data: [...] }`; unwrap to the `data` array. */
export const unwrapAssociationData = (value: unknown): unknown =>
  R.isPlainObject(value) && 'data' in value ? value.data : value;

// We flatten `fieldChanges.data` onto the root rawData object when we save editHistories
const FlattenedEditHistory = z.looseObject({
  ...EditHistory.shape,
  fieldChanges: EditHistoryFieldChange.array().nullish(),
});
export type FlattenedEditHistory = FlattenRefs<EditHistory>;

// The value of this field is the sum of the days included in the series:
//  Sun = 2, Mon = 4, Tue = 8, Wed = 16, Thur = 32, Fri = 64, Sat = 128.
//  For example, a meeting that occurs on Monday and Friday would have a recurrenceDayBits value of 68 (4+64).
const recurrenceDayBits = z.number().int().min(0).max(255);
const dayBitValues = {
  sun: 0b10,
  mon: 0b100,
  tue: 0b1000,
  wed: 0b10000,
  thu: 0b100000,
  fri: 0b1000000,
  sat: 0b10000000,
} as const satisfies Record<DayOfWeek, number>;

const recurrenceDayBitsToDays = z.codec(recurrenceDayBits, DayOfWeek.array(), {
  // Return the list of days that match the given bit using a bitwise AND ( & )
  decode: (bits) => DayOfWeek.options.filter((day) => (bits & dayBitValues[day]) !== 0),
  // Combine the bit values for each day with a bitwise OR ( | )
  encode: R.reduce((acc, day) => acc | dayBitValues[day], 0),
});

const appointmentAndTaskCommonFields = {
  type: z.string().nullish(),
  subject: z.string().nullish(),
  jobOrder: JobOrder.nullish(),
  lead: UnknownEntity.nullish(),
  opportunity: UnknownEntity.nullish(),
  placement: Placement.nullish(),
  owner: CorporateUser.nullish(),
  recurrenceStyle: z.string().nullish(),
  recurrenceType: z
    .enum(['D', 'W', 'M', 'A'])
    .nullish()
    .describe('The type of recurrence. D=daily, W=weekly, M=monthly, A=annually.'),
  recurrenceFrequency: z.number().nullish(),
  recurrenceDayBits: recurrenceDayBits.nullish(),
  notificationMinutes: z.number().nullish(),
  description: z.string().nullish(),
  dateBegin: BHDate.nullish(),
  dateEnd: BHDate.nullish(),
  isPrivate: z.boolean().nullish(),
  isDeleted: z.boolean().nullish(),
} as const;

export const Task = BaseEntity.safeExtend({
  assignees: toMany(CorporateUser).nullish(),
  candidate: Candidate.nullish(),
  childTaskOwners: toMany(CorporateUser).nullish().describe('Users assigned to childTasks'),
  childTasks: ToManyValue,
  clientContact: ClientContact.nullish(),
  clientContactReferences: toMany(ClientContact).nullish(),
  dateCompleted: BHDate.nullish(),
  isCompleted: z.boolean().nullish(),
  parentTask: UnknownEntity.nullish().describe(
    'Used when the task is assigned to someone other than the owner. The childTask is a copy of the parentTask managed by the assignee.'
  ),
  priority: z.number().int().describe('1 = Low, 2 = Normal, 3 = High'),
  taskUUID: z.string().nullish(),
  timeZoneID: z.string().nullish(),
  ...appointmentAndTaskCommonFields,
});
export type Task = z.infer<typeof Task>;

export const AppointmentAttendee = BaseEntity.safeExtend({
  appointment: UnknownEntity,
  attendee: Person,
  acceptanceStatus: z
    .number()
    .int()
    .nullish()
    .describe('-1 = declined, 0 or null = has not responded, 1 = accepted, and 2 = tentative.'),
});
export type AppointmentAttendee = z.infer<typeof AppointmentAttendee>;

export const Appointment = BaseEntity.safeExtend({
  attendees: toMany(AppointmentAttendee).nullish(),
  appointmentUUID: z.string().nullish(),
  candidateReference: Candidate.nullish(),
  clientContactReference: ClientContact.nullish(),
  childAppointments: ToManyValue.nullish(),
  communicationMethod: z.string().nullish(),
  isAllDay: z.boolean().nullish(),
  location: z.string().nullish(),
  parentAppointment: UnknownEntity.nullish(),
  sendNotification: z.boolean().nullish(),
  ...appointmentAndTaskCommonFields,
});
export type Appointment = z.infer<typeof Appointment>;

export const CandidateReference = BaseEntity.safeExtend({
  candidate: Candidate.nullish(),
  candidateTitle: z.string().nullish(),
  clientCorporation: ClientCorporation.nullish(),
  referenceClientContact: ClientContact.nullish(),
  companyName: z.string().nullish(),
  employmentStart: BHDate.nullish(),
  employmentEnd: BHDate.nullish(),
  jobOrder: JobOrder.or(z.number().int()).nullish(),
  referenceEmail: z.string().nullish(),
  referenceFirstName: z.string().nullish(),
  referenceLastName: z.string().nullish(),
  referencePhone: z.string().nullish(),
  referenceTitle: z.string().nullish(),
  status: z.string().nullish(),
  yearsKnown: z.number().nullish(),
});
export type CandidateReference = z.infer<typeof CandidateReference>;

// fields that are linked to related entities
export const CandidateAssociationFieldName = z.enum([
  'submissions',
  'placements',
  'webResponses',
  'sendouts',
]);
export type CandidateAssociationFieldName = z.infer<typeof CandidateAssociationFieldName>;

export const Specialty = BaseEntityWithName.safeExtend({
  enabled: z.boolean().nullish(),
  parentCategory: BaseEntityWithName.nullish(),
});
export type Specialty = z.infer<typeof Specialty>;

export const ReferenceEntity = Specialty;
export type ReferenceEntity = Specialty;

export const Skill = BaseEntityWithName.safeExtend({
  enabled: z.boolean().nullish(),
  categories: toMany(BaseEntityWithName).nullish(),
});
export type Skill = z.infer<typeof Skill>;

export const Category = BaseEntityWithName.safeExtend({
  enabled: z.boolean().nullish(),
  description: z.string().nullish(),
  type: z.string().nullish(),
  externalID: z.string().nullish(),
  occupation: z.string().nullish(),
  skills: toMany(BaseEntityWithName).nullish(),
  specialties: toMany(BaseEntityWithName).nullish(),
});
export type Category = z.infer<typeof Category>;

export const BULLHORN_API_ENTITIES = {
  Appointment,
  AppointmentAttendee,
  EditHistory,
  EditHistoryFieldChange,
  Task,
  Candidate,
  Person,
  CandidateReference,
  ClientContact,
  ClientCorporation,
  CorporateUser,
  JobOrder,
  JobSubmission,
  Note,
  NoteEntity,
  Placement,
  UserMessage,
  ReferenceEntity,
  Specialty,
  Skill,
  Category,
} as const;

type EntityTypes = {
  [K in keyof typeof BULLHORN_API_ENTITIES]: z.infer<(typeof BULLHORN_API_ENTITIES)[K]>;
};

type AnyEntityName = keyof typeof BULLHORN_API_ENTITIES;
type AnyNamedEntity = EntityTypes[AnyEntityName];
export type EntityForName<T extends AnyEntityName | `${string}EditHistory`> =
  T extends `${string}EditHistory` ? EditHistory
  : T extends AnyEntityName ? EntityTypes[T]
  : never;
export type EntityName<T extends AnyNamedEntity = AnyNamedEntity> =
  T extends EntityForName<infer U> ? U : never;
